import { Money } from "@despejo/domain";

/**
 * Reconciliation against Hacienda's prefilled draft (plan §5.6, §15.4).
 * The output is not just a number: every field difference must be explained
 * (automatically from known adjustments, or by the user), and a return is not
 * READY_TO_FILE while any difference is unexplained (PREFILL_DIFF_UNEXPLAINED).
 */

export type DiffCause = "MISSING_INVOICE" | "UNACCEPTED_INVOICE" | "WRONG_CABYS" | "PERSONAL_PURCHASE" | "TIMING" | "MANUAL_ADJUSTMENT" | "ROUNDING" | "OTHER";

export interface Candidate {
  /** An explanation the platform can prove from its own data. */
  cause: DiffCause;
  field: string;
  /** Signed effect on the field: platform minus draft. */
  amount: string;
  refs: string[];
  note: string;
}

export interface UserExplanation {
  field: string;
  cause: DiffCause;
  amount: string;
  note: string;
}

export interface FieldDiff {
  field: string;
  platform: string;
  draft: string;
  diff: string;
  explainedBy: (Candidate | (UserExplanation & { refs: string[] }))[];
  unexplained: string;
  status: "MATCH" | "EXPLAINED" | "UNEXPLAINED";
}

export interface ReconciliationResult {
  fields: FieldDiff[];
  unexplainedCount: number;
  readyToFile: boolean;
}

const TOLERANCE = Money.of("1"); // ₡1: form rounding

/** Find a subset of candidates whose amounts add up to the diff exactly (small N, exhaustive). */
function exactSubset(target: Money, cands: Candidate[]): Candidate[] | null {
  const n = Math.min(cands.length, 16);
  let best: Candidate[] | null = null;
  for (let mask = 1; mask < 1 << n; mask++) {
    let sum = Money.zero();
    const pick: Candidate[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        sum = sum.add(Money.of(cands[i]!.amount));
        pick.push(cands[i]!);
      }
    }
    if (sum.sub(target).abs().cmp(TOLERANCE) <= 0 && (!best || pick.length < best.length)) best = pick;
  }
  return best;
}

export function reconcile(
  platform: Record<string, string>,
  draft: Record<string, string>,
  candidates: readonly Candidate[] = [],
  userExplanations: readonly UserExplanation[] = [],
): ReconciliationResult {
  const fields = [...new Set([...Object.keys(platform), ...Object.keys(draft)])].sort();
  const out: FieldDiff[] = [];
  for (const field of fields) {
    const p = Money.of(platform[field] ?? "0");
    const d = Money.of(draft[field] ?? "0");
    const diff = p.sub(d);
    if (diff.abs().cmp(TOLERANCE) <= 0) {
      out.push({ field, platform: p.toFixed(2), draft: d.toFixed(2), diff: diff.toFixed(2), explainedBy: diff.isZero() ? [] : [{ cause: "ROUNDING", field, amount: diff.toString(), refs: [], note: "Redondeo (≤ ₡1)" }], unexplained: "0.00", status: "MATCH" });
      continue;
    }
    const auto = exactSubset(diff, candidates.filter((c) => c.field === field)) ?? [];
    let remaining = diff.sub(Money.sum(auto.map((c) => Money.of(c.amount))));
    const user: (UserExplanation & { refs: string[] })[] = [];
    for (const u of userExplanations.filter((x) => x.field === field)) {
      if (remaining.abs().cmp(TOLERANCE) <= 0) break;
      user.push({ ...u, refs: [] });
      remaining = remaining.sub(Money.of(u.amount));
    }
    const explained = remaining.abs().cmp(TOLERANCE) <= 0;
    out.push({
      field,
      platform: p.toFixed(2),
      draft: d.toFixed(2),
      diff: diff.toFixed(2),
      explainedBy: [...auto, ...user],
      unexplained: explained ? "0.00" : remaining.toFixed(2),
      status: explained ? "EXPLAINED" : "UNEXPLAINED",
    });
  }
  const unexplainedCount = out.filter((f) => f.status === "UNEXPLAINED").length;
  return { fields: out, unexplainedCount, readyToFile: unexplainedCount === 0 };
}
